// ===========================================
// Extra Lesson Requests React Query Hooks
// ===========================================
// Custom hooks for extra lesson request operations

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import {
  extraLessonApi,
  ExtraLessonRequestStatus,
  AdminBookOnBehalfInput,
  RescheduleExtraLessonInput,
} from '../api/booking.api';
import { calendarKeys } from './useHybridBooking';

export interface ExtraLessonFilters {
  status?: ExtraLessonRequestStatus;
  studentId?: string;
}

// ===========================================
// QUERY KEYS
// ===========================================

export const extraLessonKeys = {
  all: ['extraLessonRequests'] as const,
  lists: () => [...extraLessonKeys.all, 'list'] as const,
  list: (filters?: ExtraLessonFilters) =>
    [...extraLessonKeys.lists(), filters] as const,
  details: () => [...extraLessonKeys.all, 'detail'] as const,
  detail: (id: string) => [...extraLessonKeys.details(), id] as const,
  counts: () => [...extraLessonKeys.all, 'counts'] as const,
  studentMakeupOptions: (studentId: string) =>
    [...extraLessonKeys.all, 'studentMakeupOptions', studentId] as const,
  // Admin slot-picker keys — namespaced here so mutation invalidations match by
  // prefix without hand-typed string literals (was ['adminTeacherAvailableSlots']).
  adminAvailability: () => [...extraLessonKeys.all, 'adminAvailability'] as const,
  adminAvailabilityDays: (teacherId: string) =>
    [...extraLessonKeys.adminAvailability(), 'days', teacherId] as const,
  adminAvailableSlots: (teacherId: string, date?: string, duration?: number) =>
    [...extraLessonKeys.adminAvailability(), 'slots', teacherId, date, duration] as const,
};

/**
 * The date string `adminAvailableSlots` must be keyed on.
 *
 * `bookingApi.adminGetAvailableSlots` accepts `Date | string`: a string passes
 * straight through, while a Date is normalised with LOCAL getters to
 * YYYY-MM-DD. So keying a Date on `.toISOString()` stores the response under a
 * DIFFERENT day west of UTC than the one actually requested — two cache entries
 * for one query, and a key that names the wrong date. Always key through this.
 *
 * ⚠️ LOCAL getters are deliberate — do NOT "fix" this to getUTC*. The invariant
 * is "the key equals the string the API receives", and the API's Date branch
 * uses LOCAL getters. RescheduleClassDialog's own toDateKey IS UTC-based and
 * disagrees with this west of UTC — that is fine, because it passes a STRING to
 * both its key and the API, so its key names its query by construction. Making
 * this UTC would break the two Date-passing sites to match a site that never
 * calls it.
 */
export function slotDateKey(date: Date | string): string {
  if (typeof date === 'string') return date;
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(
    date.getDate()
  ).padStart(2, '0')}`;
}

// ===========================================
// QUERY HOOKS
// ===========================================

/**
 * Get all extra lesson requests with optional filters
 */
export function useExtraLessonRequests(filters?: ExtraLessonFilters) {
  return useQuery({
    queryKey: extraLessonKeys.list(filters),
    queryFn: () => extraLessonApi.adminGetRequests(filters),
  });
}

/**
 * Get pending count of extra lesson requests
 */
export function useExtraLessonPendingCount() {
  return useQuery({
    queryKey: extraLessonKeys.counts(),
    queryFn: async () => {
      const count = await extraLessonApi.adminGetPendingCount();
      return { count };
    },
  });
}

/**
 * Get single extra lesson request by ID
 */
export function useExtraLessonRequestDetail(requestId: string) {
  return useQuery({
    queryKey: extraLessonKeys.detail(requestId),
    queryFn: () => extraLessonApi.adminGetRequest(requestId),
    enabled: !!requestId,
  });
}

/**
 * The classes a student can be booked a make-up FOR, each with its term's credit
 * balance and last valid day (admin; holiday make-ups, 2026-09-28).
 */
export function useAdminStudentMakeupOptions(studentId: string) {
  return useQuery({
    queryKey: extraLessonKeys.studentMakeupOptions(studentId),
    queryFn: () => extraLessonApi.adminGetStudentMakeupOptions(studentId),
    enabled: !!studentId,
  });
}

// ===========================================
// MUTATION HOOKS
// ===========================================

/**
 * Approve an extra lesson request
 */
export function useApproveExtraLessonRequest() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: (data: { requestId: string; adminNotes?: string }) =>
      extraLessonApi.adminApprove(data.requestId, data.adminNotes),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: extraLessonKeys.all });
      enqueueSnackbar('Request approved. Parent will be notified.', {
        variant: 'success',
      });
    },
    onError: (error: Error & { response?: { data?: { message?: string } } }) => {
      const message = error.response?.data?.message || 'Failed to approve';
      enqueueSnackbar(message, { variant: 'error' });
    },
  });
}

/**
 * Reject an extra lesson request
 */
export function useRejectExtraLessonRequest() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: (data: { requestId: string; adminNotes: string }) =>
      extraLessonApi.adminReject(data.requestId, data.adminNotes),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: extraLessonKeys.all });
      enqueueSnackbar('Request rejected. Parent will be notified.', {
        variant: 'info',
      });
    },
    onError: (error: Error & { response?: { data?: { message?: string } } }) => {
      const message = error.response?.data?.message || 'Failed to reject';
      enqueueSnackbar(message, { variant: 'error' });
    },
  });
}

/**
 * Schedule an approved extra lesson request
 */
export function useScheduleExtraLessonRequest() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: (data: {
      requestId: string;
      scheduledDate: string;
      scheduledTime: string;
      duration: number;
      teacherId: string;
      roomId?: string;
    }) =>
      extraLessonApi.adminSchedule(data.requestId, {
        scheduledDate: data.scheduledDate,
        scheduledTime: data.scheduledTime,
        duration: data.duration,
        teacherId: data.teacherId,
        roomId: data.roomId,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: extraLessonKeys.all });
      queryClient.invalidateQueries({ queryKey: calendarKeys.all });
      queryClient.invalidateQueries({ queryKey: extraLessonKeys.adminAvailability() });
      enqueueSnackbar('Lesson scheduled. Parent will be notified.', {
        variant: 'success',
      });
    },
    onError: (error: Error & { response?: { data?: { message?: string } } }) => {
      const message = error.response?.data?.message || 'Failed to schedule';
      enqueueSnackbar(message, { variant: 'error' });
    },
  });
}

/**
 * Reschedule a SCHEDULED/APPROVED extra lesson to a new date/time.
 *
 * Distinct from useBooking's useAdminRescheduleExtraLesson: this variant
 * invalidates extraLessonKeys.all (the key the requests page lists on) so the
 * table refreshes after a reschedule from that page.
 */
export function useRescheduleExtraLessonRequest() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: (data: { requestId: string; input: RescheduleExtraLessonInput }) =>
      extraLessonApi.adminReschedule(data.requestId, data.input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: extraLessonKeys.all });
      queryClient.invalidateQueries({ queryKey: calendarKeys.all });
      queryClient.invalidateQueries({ queryKey: extraLessonKeys.adminAvailability() });
      enqueueSnackbar('Lesson rescheduled. Parent will be notified.', {
        variant: 'success',
      });
    },
    onError: (error: Error & { response?: { data?: { message?: string } } }) => {
      const message = error.response?.data?.message || 'Failed to reschedule';
      enqueueSnackbar(message, { variant: 'error' });
    },
  });
}

/**
 * Mark an extra lesson request as completed
 */
export function useCompleteExtraLessonRequest() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: (requestId: string) => extraLessonApi.adminComplete(requestId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: extraLessonKeys.all });
      enqueueSnackbar('Lesson marked as completed. Parent will be notified.', {
        variant: 'success',
      });
    },
    onError: (error: Error & { response?: { data?: { message?: string } } }) => {
      const message = error.response?.data?.message || 'Failed to complete';
      enqueueSnackbar(message, { variant: 'error' });
    },
  });
}

/**
 * Admin books an extra lesson on behalf of a family
 */
export function useAdminBookExtraLesson() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: (input: AdminBookOnBehalfInput) =>
      extraLessonApi.adminBookOnBehalf(input),
    onSuccess: () => {
      // extraLessonKeys.all covers the studentMakeupOptions sub-keys too, so a
      // credit consumed by this booking is refetched and the balances update.
      queryClient.invalidateQueries({ queryKey: extraLessonKeys.all });
      queryClient.invalidateQueries({ queryKey: calendarKeys.all });
      queryClient.invalidateQueries({ queryKey: extraLessonKeys.adminAvailability() });
      enqueueSnackbar('Extra lesson request created successfully.', {
        variant: 'success',
      });
    },
    onError: (error: Error & { response?: { data?: { message?: string } } }) => {
      const message = error.response?.data?.message || 'Failed to create booking';
      enqueueSnackbar(message, { variant: 'error' });
    },
  });
}

/**
 * Admin cancels a SCHEDULED/APPROVED extra lesson (refunds a consumed credit).
 */
export function useAdminCancelExtraLesson() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: (data: { requestId: string; reason?: string; notifyParents?: boolean }) =>
      extraLessonApi.adminCancel(data.requestId, data.reason, data.notifyParents),
    onSuccess: () => {
      // extraLessonKeys.all covers studentMakeupOptions, so a refunded credit's
      // balance refreshes; calendar + availability refresh as the slot is released.
      queryClient.invalidateQueries({ queryKey: extraLessonKeys.all });
      queryClient.invalidateQueries({ queryKey: calendarKeys.all });
      queryClient.invalidateQueries({ queryKey: extraLessonKeys.adminAvailability() });
      enqueueSnackbar('Extra lesson cancelled.', { variant: 'success' });
    },
    onError: (error: Error & { response?: { data?: { message?: string } } }) => {
      const message = error.response?.data?.message || 'Failed to cancel extra lesson';
      enqueueSnackbar(message, { variant: 'error' });
    },
  });
}

/**
 * Link an invoice to an extra lesson request
 */
export function useLinkInvoiceToExtraLesson() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: (data: { requestId: string; invoiceId: string }) =>
      extraLessonApi.adminLinkInvoice(data.requestId, data.invoiceId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: extraLessonKeys.all });
      enqueueSnackbar('Invoice linked successfully', {
        variant: 'success',
      });
    },
    onError: (error: Error & { response?: { data?: { message?: string } } }) => {
      const message = error.response?.data?.message || 'Failed to link invoice';
      enqueueSnackbar(message, { variant: 'error' });
    },
  });
}
